import { useQuery } from '@tanstack/react-query';
import { useRouter, useSearch } from '@tanstack/react-router';
import { documentation } from '@cwos/api-client';
import type { documentation as DocumentationNS } from '@cwos/api-client';
import {
  FilteredEmpty,
  FilterSummary,
  NARROWING_UNKNOWN,
  Pill,
  Refused,
  SuccessEmpty,
  TableCard,
  type Column,
} from '@cwos/ui-admin';
import { useAuth } from '../../lib/auth.js';
import { useNav } from '../navigation.js';
import { scopeNoteFor } from '../readScope.js';
import { UNKNOWN_POPULATION, narrowedTo, ORGANIZATION, type Population } from '../figureState.js';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import {
  DOC_STATES,
  FILTER_PILLS,
  ageLabel,
  groupSessionLabel,
  isOpen,
  stateLabel,
  statePillVariant,
  type DocFilter,
  type DocState,
} from './documentationLogic.js';

type Item = DocumentationNS.DocumentationItem;

const LIST_PAGE_SIZE = 200;

function parseFilter(search: { state?: string; late?: string }): DocFilter {
  const state = DOC_STATES.includes(search.state as DocState)
    ? (search.state as DocState)
    : undefined;
  return { state, isLateOnly: search.late === '1' };
}

/** The one tail every branch of the blurb shares, so the frame is the only variable. */
const NON_PUNITIVE = 'Nothing here is punitive — it shows the work still owed, honestly.';

/**
 * WHAT THIS SCREEN IS, said once per frame.
 *
 * A `switch` over the union rather than a ternary over a boolean, so a fourth
 * `Population` could not be added without the compiler stopping here. The
 * boolean this replaced had no room for `unknown` at all, which is how the
 * organization-wide sentence came to be printed on every cold load.
 */
function blurb(population: Population): string {
  switch (population.kind) {
    case 'organization':
      return `Every delivered visit in your organization and where its note stands. ${NON_PUNITIVE}`;
    case 'narrowed':
      return `Delivered visits for ${population.who}, and where each note stands. ${NON_PUNITIVE}`;
    case 'unknown':
      // Says what is true of every frame and claims neither. Not "your
      // organization", which is the guess that does damage, and not "your own",
      // which calls an org_admin narrowed while their permission set is still in
      // flight.
      return `Delivered visits your account can see, and where each note stands. We could not check how far that reaches. ${NON_PUNITIVE}`;
  }
}

/**
 * THE ZERO, AND WHOSE ZERO IT IS.
 *
 * `SuccessEmpty` sets its message in the same typeface as an answer, and the
 * answer it imitates — "there is no work outstanding" — is the reassuring one.
 * Over a scoped read it is false about the organization while being true about
 * every row that was not returned.
 */
function emptyMessage(population: Population): string {
  switch (population.kind) {
    case 'organization':
      return 'There are no delivered visits in the records yet.';
    case 'narrowed':
      return `There are no delivered visits among ${population.who}. Someone with organization-wide access may see more.`;
    case 'unknown':
      return 'There are no delivered visits in the records your account can see. We could not check how far that reaches, so someone else may see more.';
  }
}

/**
 * Documentation queue (doc 01 A5.2) — open notes org-wide, non-punitive.
 *
 * Reads the real `GET /documentation` surface (gated `documentation:read`):
 * every delivered visit in the tenant with its documentation state. The state
 * filter pills drive server-side filtering through the URL (URL-as-truth), and
 * each row opens the record's own page — there are no fake saves and no dead
 * buttons on this screen: it is a read surface, and it only reads.
 */
export function DocumentationQueue() {
  const { me } = useAuth();
  // No permission read here at all any more. The SERVER decides what this list
  // contains AND says so on the payload, so the heading tells the truth about
  // which list arrived by reading the list, not by re-deriving the decision that
  // produced it. See `population` below.
  const router = useRouter();
  const navigate = useNav();
  const search = useSearch({ strict: false }) as { state?: string; late?: string };
  const filter = parseFilter(search);

  const list = useQuery({
    queryKey: ['documentation', 'list', filter.state ?? 'all', filter.isLateOnly ? 'late' : 'all'],
    queryFn: () =>
      documentation.list({
        state: filter.state,
        isLate: filter.isLateOnly ? true : undefined,
        limit: LIST_PAGE_SIZE,
      }),
  });

  const items = list.data?.items ?? [];
  /**
   * THREE NUMBERS, THREE PROBLEMS, ONE LINE.
   *
   * It read `{total} records · {openCount} open · {lateCount} late`, rendered
   * ABOVE the loading/error branch — so on a cold load, on a 500 and on a 403 a
   * compliance reader saw "0 records · 0 open · 0 late" beside a skeleton or an
   * error panel. `total ?? items.length` supplied the zero.
   *
   * The second problem survives a successful response: `total` is the server's
   * count of the whole scoped set, while `openCount` and `lateCount` are
   * computed over `items`, which this screen caps at LIST_PAGE_SIZE. Past the
   * cap two of the three numbers silently describe a smaller population than
   * the first, with no pager and nothing saying so.
   *
   * The third is the scope, and it is the one that took three passes.
   *
   * ONE FRAME, NOT TWO. `documentationPopulation` was moved onto `can()` — the
   * scope-aware question, matching `routes.ts:82`'s `can(principal.authz,
   * PERMISSION_ALL)` — and that closed the FilterSummary. It did not close this
   * screen, because the screen was asking the question TWICE and only one of the
   * two was fixed: a separate `canReadAll = has('documentation:read_all')` was
   * still framing the blurb at the top and the empty state at the bottom off the
   * FLATTENED key list. So a `documentation:read_all` devolved to one program
   * got "Every delivered visit in your organization" over a list the server had
   * narrowed, and "There are no delivered visits in the records yet" over an
   * empty one — a scoped refusal read back as an organization with no work
   * outstanding, which is the reassuring direction and the one nobody checks.
   *
   * AND THEN: THERE IS NO NEED TO RECONSTRUCT IT AT ALL. `readScope.ts` calls
   * `documentationPopulation` "the last mirror" on the grounds that
   * `GET /documentation` does not state its scope. It does. `listDocumentation`
   * returns `{ items, total, scoped }` with `scoped: scope.kind !== 'organization'`
   * (documentation/service.ts:207), the route replies with the whole object
   * (routes.ts:145), and `DocumentationListResponse.scoped` in
   * `@cwos/api-client` is already documented as "True when `total` counts the
   * caller's scope, not the organization". The frame was on the wire the whole
   * time in a different spelling — a boolean rather than the
   * `'organization' | 'caseload'` union the other three domains publish — which
   * is why a search for the union did not find it.
   *
   * WHY THE PAYLOAD BEATS THE MIRROR, and it is not only tidiness. `auth.can()`
   * falls back to the flattened `permissions` list whenever `permissionScopes`
   * is absent — an API deployed before that field, or a `/auth/me` that has not
   * answered — and in that fallback a program-confined grant reads as
   * organization-wide again. That is a knowingly permissive fallback and the
   * right one for a GATE; it is the original defect for a SENTENCE. `scoped` is
   * decided by the server on the same request that produced `total`, so it is
   * right regardless of API version and cannot go stale against a grant revoked
   * since the permission set was cached.
   *
   * Three values, not two. `undefined` — in flight, failed, or refused — is
   * `unknown`, which is the whole reason `Population` has a third case.
   */
  const population: Population =
    list.data === undefined
      ? UNKNOWN_POPULATION
      : list.data.scoped
        ? narrowedTo('the workers you can see')
        : ORGANIZATION;
  const total = list.data?.total ?? null;
  const openCount = list.data === undefined ? null : items.filter((i) => isOpen(i.state)).length;
  const lateCount = list.data === undefined ? null : items.filter((i) => i.isLate).length;
  const capped = total !== null && items.length < total;
  const filtered = filter.state !== undefined || filter.isLateOnly;

  const setFilter = (next: DocFilter) => {
    const searchNext: { state?: string; late?: string } = {};
    if (next.state) searchNext.state = next.state;
    if (next.isLateOnly) searchNext.late = '1';
    void router.navigate({ to: '/documentation', search: searchNext });
  };

  const scope = {
    what: 'delivered visits',
    scope: me ? (me.orgName ?? me.orgId) : 'your organization',
    period: 'from the service record',
    narrowings: [
      ...(filter.state ? [{ label: stateLabel(filter.state) }] : []),
      ...(filter.isLateOnly ? [{ label: 'late only' }] : []),
    ],
    // The summary named the ORGANIZATION as the scope of a list the server
    // narrows to the workers this reader may see, and then promised "That's
    // everything — no hidden filters" under it.
    //
    // THREE VALUES, BECAUSE `population` HAS THREE. `scopeNoteFor` collapses
    // two of them into a sentence and returns `null` only for `organization`,
    // so `?? undefined` mapped `unknown` onto the SAME string channel as
    // `narrowed` — and `FilterSummary` printed "Not everything — your access
    // narrows this to what your account reaches" on every cold load, before
    // `list.data` had arrived to say whether anything narrowed it at all. That
    // is an assertion that this list is INCOMPLETE, made about a list that may
    // be complete, by the one component doc 10.6 puts on every list screen to
    // be believed about what is on screen. A guess in the alarming direction is
    // still a guess.
    //
    // `NARROWING_UNKNOWN` is that third channel and it renders NEITHER
    // sentence — not the completeness promise and not its denial. The summary
    // still says what is shown, from where, over what period, and stops there
    // until the response says whose list this is.
    //
    // `scopeNoteFor` IS DELIBERATELY NOT WIDENED. Four other screens
    // (SupervisionCenter, CredentialsCenter, TimeCorrections, people/board)
    // consume its return as a plain sentence, and its `unknown` arm — "what
    // your account reaches — we could not check how far that is" — is the RIGHT
    // answer for a `<ScopeNote>`, which is a standing caveat rather than a
    // completeness claim. The mismatch is not in the helper; it is in this call
    // site handing a three-state frame to what used to be a two-state channel.
    // So the branch is taken here, where the channel is.
    narrowedBy:
      population.kind === 'unknown' ? NARROWING_UNKNOWN : (scopeNoteFor(population) ?? undefined),
  };

  const columns: Column<Item>[] = [
    {
      key: 'visit',
      header: 'Visit',
      render: (r) => (
        <span>
          {new Date(r.occurredAt).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
          })}
          <span className="rn-muted" style={{ marginLeft: 'var(--space-8)' }}>
            {ageLabel(r.occurredAt)}
          </span>
        </span>
      ),
    },
    { key: 'client', header: 'Client', render: (r) => r.clientInitials },
    {
      key: 'worker',
      header: 'Worker',
      // "Unassigned" asserts that nobody delivered the visit. `workerId` and
      // `workerDisplayName` are separate nullable fields, and only the first
      // answers that question — a row with an id and no name is a person this
      // reader was not given a name for, which is a different sentence. And a
      // null `workerId` with several `workerIds` is a group session: several
      // people owe it, which is not nobody.
      render: (r) =>
        r.workerDisplayName ? (
          r.workerDisplayName
        ) : r.workerIds.length > 1 ? (
          groupSessionLabel(r.workerIds.length)
        ) : r.workerId ? (
          <span className="rn-muted">Name not shown to you</span>
        ) : (
          <span className="rn-muted">Unassigned</span>
        ),
    },
    {
      key: 'state',
      header: 'Documentation',
      render: (r) => (
        <Pill variant={statePillVariant(r.state)} dot>
          {stateLabel(r.state)}
        </Pill>
      ),
    },
    {
      key: 'lag',
      header: 'Outstanding',
      render: (r) =>
        r.lagDays === null ? (
          <span className="rn-muted">—</span>
        ) : (
          `${r.lagDays}d${r.isLate ? ' · late' : ''}`
        ),
      numeric: true,
    },
  ];

  return (
    <div className="rn-col" style={{ maxWidth: 960 }}>
      <h1 className="rn-card-title" style={{ fontSize: '1.125rem' }}>
        Documentation
      </h1>
      <p className="rn-muted">
        {/* Whose records these are is part of what the numbers mean. The server
            scopes this list — organization-wide only for a `documentation:read_all`
            held over the ORGANIZATION, otherwise the reader and the workers they
            can see — so the blurb has to say which, or a worker reads their own
            five as the whole organization's backlog.

            Three branches, not two. Before the set has arrived the frame is not
            known, and both confident sentences are a guess: "your organization"
            is the guess that does damage, and the other accuses an org-wide
            admin of being narrowed on every cold load. */}
        {blurb(population)}
      </p>

      <FilterSummary scope={scope} />

      {/* The same segmented control Needs attention uses, and for the same
          reason: these were seven `.rn-link` buttons in a row, all link-blue
          and all identical whether pressed or not, so the screen never said
          which filter was in force. The buttons and their `aria-pressed`
          contract are unchanged. */}
      <div className="rn-tabs" role="group" aria-label="Filter documentation">
        {FILTER_PILLS.map((pill) => (
          <button
            key={pill.key}
            type="button"
            className="rn-tab"
            onClick={() => setFilter(pill.next(filter))}
            aria-pressed={pill.active(filter)}
          >
            {pill.label}
          </button>
        ))}
      </div>
      {/* One sentence of counts, which does not need a card around it. The box
          it used to sit in was 80px of chrome for a line of muted text. */}
      <p className="rn-muted" style={{ margin: '0 0 var(--space-16)' }}>
        {total === null
          ? 'Counts not read yet'
          : `${total} records · ${openCount ?? '—'} open · ${lateCount ?? '—'} late`}
        {capped
          ? ` · open and late are counted over the ${items.length} shown, not all ${total}`
          : ''}
      </p>

      <ListState
        phase={listPhase(list, list.data === undefined ? undefined : items.length)}
        surface="table"
        rows={6}
        failedMessage="We couldn’t load documentation just now. This is not a statement that no notes are owed."
        onRetry={() => void list.refetch()}
        refused={
          <Refused
            heading="The documentation record is not shown to you"
            message="Your account does not have access to this list. It is not an organization with no notes outstanding."
          />
        }
        empty={
          filtered ? (
            <FilteredEmpty onClear={() => setFilter({})} />
          ) : (
            // The zero, and the sentence that says whose zero it is. "There are
            // no delivered visits in the records yet" is a statement about the
            // organization, and an empty page is exactly where a reader stops
            // asking.
            <SuccessEmpty heading="Nothing outstanding" message={emptyMessage(population)} />
          )
        }
      >
        <TableCard
          columns={columns}
          rows={items}
          rowKey={(r) => r.serviceEventId}
          onRowOpen={(r) => navigate(`/documentation/${r.serviceEventId}`)}
          caption="Delivered visits and their documentation state"
        />
      </ListState>
    </div>
  );
}
